import { useEffect, useMemo, useState } from "react";

/**
 * A single enrollment row shown in a bulk-action modal's enrollment table.
 * Shared by every Bulk*Modal so the shape (and its derivation) is defined once.
 */
export interface EnrollmentRow {
  enrollmentId: number;
  enquiryId: number;
  studentCode?: string | null;
  statusId?: number | null;
  statusName?: string | null;
  batchName?: string | null;
  studentName?: string | null;
}

/**
 * Flattens the enrollments under the selected enquiries (joining student names
 * from `studentList`) and tracks which of them are checked in the modal.
 *
 * Default selection = ALL enrollments (preserves the historic "apply to all"
 * behaviour). The selection re-seeds to all whenever the underlying enrollment
 * set changes or the modal is (re)opened (`show` toggles to true).
 *
 * Consumers submit `selectedEnrollmentIds` (the checked subset), not every row.
 */
/**
 * Selection-only hook: given an already-derived `enrollments` array, tracks the
 * checked subset (defaults to all; re-seeds when the set changes or the modal
 * opens). Used directly by modals that derive their own list (e.g. Promote,
 * which filters to REGULAR enrollments), and internally by
 * `useEnrollmentSelection`.
 */
export function useRowSelection(enrollments: EnrollmentRow[], show?: boolean) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const enrollmentKey = enrollments.map((e) => e.enrollmentId).join(",");
  useEffect(() => {
    if (show === false) return; // don't churn while hidden
    setSelectedIds(new Set(enrollments.map((e) => e.enrollmentId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentKey, show]);

  const toggle = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds((prev) =>
      prev.size === enrollments.length
        ? new Set()
        : new Set(enrollments.map((e) => e.enrollmentId)),
    );

  const allSelected = enrollments.length > 0 && selectedIds.size === enrollments.length;
  const selectedEnrollmentIds = useMemo(() => Array.from(selectedIds), [selectedIds]);

  return { selectedIds, selectedEnrollmentIds, toggle, toggleAll, allSelected };
}

export function useEnrollmentSelection(
  selectedEnquiryIds: number[],
  enquiryEnrollmentsMap: Record<number, any[]>,
  studentList: any[],
  show?: boolean,
) {
  const enrollments: EnrollmentRow[] = useMemo(() => {
    const nameByEnquiryId = new Map<number, string>();
    for (const s of studentList) nameByEnquiryId.set(s.enquiryId, s.name);

    const out: EnrollmentRow[] = [];
    for (const eqId of selectedEnquiryIds) {
      const list = enquiryEnrollmentsMap[eqId] || [];
      for (const enr of list) {
        out.push({
          enrollmentId: enr.enrollmentId,
          enquiryId: eqId,
          studentCode: enr.studentCode,
          statusId: enr.statusId,
          statusName: enr.statusName,
          batchName: enr.batchName,
          studentName: nameByEnquiryId.get(eqId) || "",
        });
      }
    }
    return out;
  }, [selectedEnquiryIds, enquiryEnrollmentsMap, studentList]);

  const selection = useRowSelection(enrollments, show);

  return { enrollments, ...selection };
}
